import { LabelWithTooltip } from '../../common/LabelWithTooltip';
import { useTranslation } from '@hooks/useTranslation';
import type { InventoryConfiguration } from '../types';
import { getScalesWithLabel } from '../scalesWithOptions';
import { getFormatLabel } from '../formatOptions';
import { ConfigurationSummary } from '../ConfigurationSummary';

interface DefaultInventorySectionProps {
  configurations: InventoryConfiguration[];
  inventoryName: string;
  showTooltip?: boolean;
}

function ConfigurationCard({ config, index, showTooltip, inventoryName }: { config: InventoryConfiguration; index: number; showTooltip: boolean; inventoryName: string }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-input border border-dinnerplate-white p-6">
      <div className="flex flex-col gap-3">
        <p className="font-inter text-lg font-semibold text-off-black">
          {t('common.default_inventory.configuration', `Configuration ${index + 1}`).replace('%{number}', String(index + 1))}
        </p>

        <div className="flex flex-col gap-8">
          {/* Row 1: Ratio + Scales with */}
          <div className="flex gap-8">
            <div className="flex flex-col gap-2 w-config-col">
              {showTooltip ? (
                <LabelWithTooltip
                  label={t('common.default_inventory.ratio')}
                  tooltip={t('common.default_inventory.ratio_tooltip')}
                  iconVariant="muted"
                  className="flex items-center gap-1.5"
                >
                  <span className="font-inter text-sm font-semibold text-grey">
                    {t('common.default_inventory.ratio')}
                  </span>
                </LabelWithTooltip>
              ) : (
                <span className="font-inter text-sm font-semibold text-grey">
                  {t('common.default_inventory.ratio')}
                </span>
              )}
              <div className="flex gap-8 font-inter text-sm text-off-black">
                <span>{t('common.default_inventory.quantity')}: {(config.quantity ?? 0).toFixed(1)}</span>
                <span>{t('common.default_inventory.units')}: {(config.value ?? 0).toFixed(1)}</span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {showTooltip ? (
                <LabelWithTooltip
                  label={t('common.default_inventory.scales_with')}
                  tooltip={t('common.default_inventory.scales_with_tooltip')}
                  iconVariant="muted"
                  className="flex items-center gap-1.5"
                >
                  <span className="font-inter text-sm font-semibold text-grey">
                    {t('common.default_inventory.scales_with')}
                  </span>
                </LabelWithTooltip>
              ) : (
                <span className="font-inter text-sm font-semibold text-grey">
                  {t('common.default_inventory.scales_with')}
                </span>
              )}
              <p className="font-inter text-sm text-off-black">
                {getScalesWithLabel(config.default_type) || '—'}
              </p>
            </div>
          </div>

          {/* Row 2: Format + Multiplied by */}
          <div className="flex gap-8">
            <div className="flex flex-col gap-2 w-config-col">
              <span className="font-inter text-sm font-semibold text-grey">
                {t('common.default_inventory.format')}
              </span>
              <p className="font-inter text-sm text-off-black">
                {getFormatLabel(config.default_event_type) || '—'}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {showTooltip ? (
                <LabelWithTooltip
                  label={t('common.default_inventory.multiplied_by')}
                  tooltip={t('common.default_inventory.multiplied_by_tooltip')}
                  iconVariant="muted"
                  className="flex items-center gap-1.5"
                >
                  <span className="font-inter text-sm font-semibold text-grey">
                    {t('common.default_inventory.multiplied_by')}
                  </span>
                </LabelWithTooltip>
              ) : (
                <span className="font-inter text-sm font-semibold text-grey">
                  {t('common.default_inventory.multiplied_by')}
                </span>
              )}
              {config.multiplier ? (
                <span className="inline-flex items-center justify-center rounded-xs border border-off-black px-1.5 py-0.5 font-inter text-xs leading-[1.25] font-normal text-off-black whitespace-nowrap shrink-0 w-fit">
                  {config.multiplier}
                </span>
              ) : (
                <p className="font-inter text-sm text-off-black">—</p>
              )}
            </div>
          </div>

          {/* Summary line */}
          <ConfigurationSummary
            defaultType={config.default_type}
            quantity={config.quantity}
            value={config.value}
            multiplier={config.multiplier}
            inventoryName={inventoryName}
          />
        </div>
      </div>
    </div>
  );
}

export function DefaultInventorySection({ configurations, inventoryName, showTooltip = true }: DefaultInventorySectionProps) {
  const { t } = useTranslation();

  if (configurations.length === 0) return null;

  return (
    <div className="mt-12">
      <div className="flex items-center gap-4 mb-4">
        {showTooltip ? (
          <LabelWithTooltip
            label={t('common.default_inventory.title')}
            tooltip={t('common.default_inventory.tooltip')}
            iconSize={24}
            className="flex items-center gap-1.5"
          >
            <h3 className="font-kapra-expanded text-2xl font-black uppercase leading-normal text-off-black">
              {t('common.default_inventory.title')}
            </h3>
          </LabelWithTooltip>
        ) : (
          <h3 className="font-kapra-expanded text-2xl font-black uppercase leading-normal text-off-black">
            {t('common.default_inventory.title')}
          </h3>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {configurations.map((config, index) => (
          <ConfigurationCard key={config.id} config={config} index={index} showTooltip={showTooltip ?? true} inventoryName={inventoryName} />
        ))}
      </div>
    </div>
  );
}
